import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Box,
  TextField,
  CardContent,
  Paper,
  useMediaQuery,
  useTheme,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Store } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { AnimatedCard, AnimatedButton, AnimatedBox, BlobCursor, PrismBackground, WaveAnimation, Particles } from '../ui';

const ContactPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    alert('Thank you for your message! We will get back to you soon.');
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      {/* Header */}
      <AppBar 
        position="sticky" 
        elevation={0}
        sx={{
          backgroundColor: 'white',
          color: '#050206',
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
        }}
      >
        <Toolbar
          sx={{
            px: { xs: 2, sm: 4, md: 6 },
            py: 1.5,
          }}
        >
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: { xs: 1, md: 0 } }}>
            <Box
              component="img"
              src="/logo.png"
              alt="DiscountDays Logo"
              onClick={() => navigate('/')}
              sx={{
                width: 40,
                height: 40,
                objectFit: 'contain',
                mr: 1.5,
                cursor: 'pointer',
              }}
            />
            <Typography 
              variant="h6" 
              component="div" 
              onClick={() => navigate('/')}
              sx={{ 
                fontWeight: 700,
                color: '#0084FF',
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                cursor: 'pointer',
              }}
            >
              DiscountDays
            </Typography>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <>
              <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', gap: 3, mx: 4 }}>
                <Button 
                  onClick={() => navigate('/features')} 
                  sx={{ 
                    color: '#050206',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    '&:hover': {
                      backgroundColor: 'rgba(59, 59, 59, 0.05)',
                    },
                  }}
                >
                  Features
                </Button>
                <Button 
                  onClick={() => navigate('/about')} 
                  sx={{ 
                    color: '#050206',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    '&:hover': {
                      backgroundColor: 'rgba(59, 59, 59, 0.05)',
                    },
                  }}
                >
                  About Us
                </Button>
                <Button 
                  onClick={() => navigate('/contact')} 
                  sx={{ 
                    color: '#050206',
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.95rem',
                    '&:hover': {
                      backgroundColor: 'rgba(59, 59, 59, 0.05)',
                    },
                  }}
                >
                  Contact
                </Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button
                variant="contained" 
                sx={{ 
                    backgroundColor: '#0084FF',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 2,
                  '&:hover': {
                      backgroundColor: '#0066CC',
                  },
                }}
                onClick={() => navigate('/login')}
              >
                Admin Login
                </Button>
              </Box>
            </>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setMobileOpen(true)}
              sx={{ ml: 'auto' }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Toolbar>

        {/* Mobile Drawer */}
        <Drawer
          anchor="right"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          PaperProps={{
            sx: {
              width: 280,
              backgroundColor: 'white',
            },
          }}
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700, color: '#3B3B3B' }}>
              Menu
            </Typography>
            <List>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { navigate('/features'); setMobileOpen(false); }}>
                  <ListItemText primary="Features" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { navigate('/about'); setMobileOpen(false); }}>
                  <ListItemText primary="About Us" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton onClick={() => { navigate('/contact'); setMobileOpen(false); }}>
                  <ListItemText primary="Contact" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding sx={{ mt: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    backgroundColor: '#0084FF',
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                  onClick={() => { navigate('/login'); setMobileOpen(false); }}
                >
                  Admin Login
                </Button>
              </ListItem>
            </List>
          </Box>
        </Drawer>
      </AppBar>

      {/* Hero Section with Blue Gradient Background */}
      <Box
        sx={{
          position: 'relative',
          background: 'linear-gradient(135deg, #0084FF 0%, #00B4FF 100%)',
          py: { xs: 8, md: 12 },
          px: { xs: 2, sm: 4 },
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            zIndex: 0,
          }}
        >
          <Particles
            particleColors={['#ffffff', '#ffffff']}
            particleCount={200}
            particleSpread={10}
            speed={0.1}
            particleBaseSize={100}
            moveParticlesOnHover={true}
            alphaParticles={false}
            disableRotation={false}
          />
        </Box>
        
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              color: 'white',
              textAlign: 'center',
            }}
          >
            <Typography 
              variant={isMobile ? 'h3' : 'h2'} 
              component="h1" 
              sx={{
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '2rem', md: '3rem' },
                animation: 'fadeInUp 0.8s ease-out',
                '@keyframes fadeInUp': {
                  '0%': { opacity: 0, transform: 'translateY(30px)' },
                  '100%': { opacity: 1, transform: 'translateY(0)' },
                },
              }}
            >
              Contact Us
            </Typography>
            <Typography 
              variant={isMobile ? 'body1' : 'h6'} 
              sx={{ 
                opacity: 0.95,
                fontSize: { xs: '1rem', md: '1.25rem' },
                animation: 'fadeInUp 0.8s ease-out 0.2s both',
              }}
            >
              We'd love to hear from you
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Contact Form and Info */}
      <Box sx={{ backgroundColor: 'white' }}>
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 }, px: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flexWrap: 'wrap', gap: { xs: 4, md: 6 } }}>
          {/* Contact Form */}
          <AnimatedBox delay={0} sx={{ flex: { xs: '1 1 100%', md: '2 1 400px' }, minWidth: { xs: '100%', md: '400px' }, width: '100%' }}>
            <AnimatedCard>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h5" component="h2" gutterBottom>
                  Send us a Message
                </Typography>
                <form onSubmit={handleSubmit}>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, flexWrap: 'wrap', gap: 3 }}>
                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: { xs: '100%', sm: '200px' }, width: '100%' }}>
                      <TextField
                        fullWidth
                        label="Your Name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                      />
                    </Box>
                    <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 200px' }, minWidth: { xs: '100%', sm: '200px' }, width: '100%' }}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                    </Box>
                    <Box sx={{ flex: '1 1 100%' }}>
                      <TextField
                        fullWidth
                        label="Subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                      />
                    </Box>
                    <Box sx={{ flex: '1 1 100%' }}>
                      <TextField
                        fullWidth
                        label="Message"
                        name="message"
                        multiline
                        rows={4}
                        value={formData.message}
                        onChange={handleChange}
                        required
                      />
                    </Box>
                    <Box sx={{ flex: '1 1 100%' }}>
                      <AnimatedButton
                        type="submit"
                        variant="contained"
                        size="large"
                        sx={{ px: 4 }}
                      >
                        Send Message
                      </AnimatedButton>
                    </Box>
                  </Box>
                </form>
              </CardContent>
            </AnimatedCard>
          </AnimatedBox>

          {/* Contact Information */}
          <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 300px' }, minWidth: { xs: '100%', md: '300px' }, width: '100%' }}>
            <Box sx={{ mb: 4 }}>
              <AnimatedCard delay={100}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Get in Touch
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Have questions or need support? We're here to help!
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>Email:</strong> info@godiscountdays.com
                  </Typography>
                  <Typography variant="body2" paragraph>
                    <strong>Phone:</strong> +1 (555) 123-4567
                  </Typography>
                  <Typography variant="body2">
                    <strong>Address:</strong> 123 Business St, City, State 12345
                  </Typography>
                </CardContent>
              </AnimatedCard>
            </Box>

            <AnimatedCard delay={200}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Business Hours
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>Monday - Friday:</strong> 9:00 AM - 6:00 PM
                </Typography>
                <Typography variant="body2" paragraph>
                  <strong>Saturday:</strong> 10:00 AM - 4:00 PM
                </Typography>
                <Typography variant="body2">
                  <strong>Sunday:</strong> Closed
                </Typography>
              </CardContent>
            </AnimatedCard>
          </Box>
        </Box>
      </Container>
      </Box>

      {/* FAQ Section */}
      <Box sx={{ backgroundColor: 'white', py: { xs: 4, md: 8 } }}>
        <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 } }}>
          <AnimatedBox delay={0}>
            <Typography variant="h4" component="h2" textAlign="center" gutterBottom>
              Frequently Asked Questions
            </Typography>
          </AnimatedBox>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flexWrap: 'wrap', gap: { xs: 3, md: 4 }, mt: 4 }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 400px' }, minWidth: { xs: '100%', md: '400px' }, width: '100%' }}>
              <AnimatedCard delay={0} sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" gutterBottom>
                  How do I become a vendor?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Simply download our vendor app, create an account, and submit your business information. Our team will review and approve your application.
                </Typography>
              </AnimatedCard>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 400px' }, minWidth: { xs: '100%', md: '400px' }, width: '100%' }}>
              <AnimatedCard delay={100} sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" gutterBottom>
                  Are all deals verified?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Yes, all deals are verified by our team to ensure authenticity and value for our customers.
                </Typography>
              </AnimatedCard>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 400px' }, minWidth: { xs: '100%', md: '400px' }, width: '100%' }}>
              <AnimatedCard delay={200} sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" gutterBottom>
                  How do I redeem deals?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Show the deal code or QR code to the vendor at the time of purchase. Each deal has specific redemption instructions.
                </Typography>
              </AnimatedCard>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 400px' }, minWidth: { xs: '100%', md: '400px' }, width: '100%' }}>
              <AnimatedCard delay={300} sx={{ p: { xs: 2, md: 3 } }}>
                <Typography variant="h6" gutterBottom>
                  Is the app free to use?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Yes, our app is completely free for customers. We make money through partnerships with vendors.
                </Typography>
              </AnimatedCard>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ backgroundColor: 'white', borderTop: '1px solid #eee', color: '#050206', py: { xs: 6, md: 8 }, px: { xs: 2, sm: 4 } }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', gap: { xs: 4, md: 6 }, mb: 4 }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 40%' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box 
                  component="img"
                  src="/logo.png"
                  alt="DiscountDays Logo"
                  sx={{ width: 40, height: 40, objectFit: 'contain', mr: 1.5 }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#0084FF' }}>DiscountDays</Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)' }}>© copyright DiscountDays 2025. All rights reserved.</Typography>
            </Box>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 3, sm: 6 }, flex: { xs: '1 1 100%', md: '1 1 60%' } }}>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: '#0084FF' }}>Socials</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)', cursor: 'pointer', '&:hover': { color: '#0084FF' } }}>Instagram</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)', cursor: 'pointer', '&:hover': { color: '#0084FF' } }}>LinkedIn</Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: '#0084FF' }}>Legal</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" onClick={() => navigate('/privacy-policy')} sx={{ color: 'rgba(0, 0, 0, 0.6)', cursor: 'pointer', '&:hover': { color: '#0084FF' } }}>Privacy Policy</Typography>
                  <Typography variant="body2" onClick={() => navigate('/delete-account')} sx={{ color: 'rgba(0, 0, 0, 0.6)', cursor: 'pointer', '&:hover': { color: '#0084FF' } }}>Delete Account</Typography>
                </Box>
              </Box>

              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1.5, color: '#0084FF' }}>Become a Partner</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(0, 0, 0, 0.6)', cursor: 'pointer', '&:hover': { color: '#0084FF' } }}>Sign Up</Typography>
                  <Typography variant="body2" onClick={() => navigate('/login')} sx={{ color: 'rgba(0, 0, 0, 0.6)', cursor: 'pointer', '&:hover': { color: '#0084FF' } }}>Login</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default ContactPage;
